import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Collapse, FormControlLabel, Grid, IconButton, InputAdornment, LinearProgress, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CategoryIcon from '@mui/icons-material/Category';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PaymentsIcon from '@mui/icons-material/Payments';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import StatTile from '../../../../common/stat_tile';
import useNotification from '../../../../../core/hooks/use_notification';
import { getWarehouseStockAction, getWarehouseStockState } from '../../../../../features/inventory/get_warehouse_stock';
import { getWarehouseStockNotificationData } from '../../../../../core/utils/notification_utils';
import { ROUTE_WAREHOUSE_STOCKS } from '../../../../../core/constants';
import PageHeader from '../../../../common/page_header';
import FilterPaper from '../../../../common/filter_paper';
import { ExpirationChip, formatCurrency, formatNumber, getBatchValue, getStockLocationName, getWarehouseTypeLabel, isExpiringSoon } from '../stock_utils';

const needsAttention = (batch) => batch?.expired || isExpiringSoon(batch?.expirationDate);


const ProductRow = ({ product, open, onToggle }) => (
    <>
        <TableRow hover onClick={ onToggle } sx={ { cursor: 'pointer', '& > td': { borderBottom: open ? 'none' : undefined } } }>
            <TableCell padding="checkbox">
                <IconButton size="small">{ open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/> }</IconButton>
            </TableCell>
            <TableCell>
                <Typography variant="body2" sx={ { fontWeight: 600 } }>{ product?.productName || `Produkt #${ product?.productId }` }</Typography>
                <Typography variant="caption" color="text.secondary">{ product?.productType }</Typography>
            </TableCell>
            <TableCell align="right">{ formatNumber(product?.quantity) }</TableCell>
            <TableCell align="right">{ product?.batches?.length }</TableCell>
            <TableCell align="center"><ExpirationChip date={ product?.nearestExpirationDate }/></TableCell>
            <TableCell align="right">
                {
                    product?.batches?.some(batch => batch?.expired)
                        ? <Chip size="small" color="error" label={ `${ formatNumber(product.batches.filter(batch => batch?.expired).reduce((sum, batch) => sum + batch.quantity, 0)) } szt.` }/>
                        : '-'
                }
            </TableCell>
        </TableRow>
        <TableRow>
            <TableCell colSpan={ 6 } sx={ { py: 0 } }>
                <Collapse in={ open } timeout="auto" unmountOnExit>
                    <Box sx={ { my: 1.5, ml: 6 } }>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={ { '& th': { color: 'text.secondary', fontWeight: 600 } } }>
                                    <TableCell>Partia</TableCell>
                                    <TableCell align="center">Termin ważności</TableCell>
                                    <TableCell align="right">Sztuki</TableCell>
                                    <TableCell align="right">Cena netto zakupu</TableCell>
                                    <TableCell align="right">Wartość</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    product?.batches?.map(batch => (
                                        <TableRow key={ `${ batch?.productBatchId }-${ batch?.warehouseBatchNumber }-${ batch?.expirationDate }` }>
                                            <TableCell>
                                                <Typography variant="body2">{ batch?.warehouseBatchNumber }</Typography>
                                                <Typography variant="caption" color="text.secondary">{ batch?.productBatchId || 'brak powiązania partii' }</Typography>
                                            </TableCell>
                                            <TableCell align="center"><ExpirationChip date={ batch?.expirationDate } expired={ batch?.expired }/></TableCell>
                                            <TableCell align="right">{ formatNumber(batch?.quantity) }</TableCell>
                                            <TableCell align="right">{ formatCurrency(batch?.purchaseNetPrice) }</TableCell>
                                            <TableCell align="right">{ formatCurrency(getBatchValue(batch)) }</TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    </>
);

const WarehouseStockPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { warehouseId } = useParams();
    const { showErrorMessage } = useNotification();
    const getWarehouseStock = useSelector(getWarehouseStockState);

    const [query, setQuery] = useState('');
    const [onlyAttention, setOnlyAttention] = useState(false);
    const [openProducts, setOpenProducts] = useState([]);

    useEffect(() => {
        dispatch(getWarehouseStockAction(warehouseId))
            .then(response => showErrorMessage(getWarehouseStockNotificationData(response)));
    }, [dispatch, warehouseId, showErrorMessage]);

    const stock = getWarehouseStock?.data;

    const products = useMemo(() => (stock?.products || [])
        .filter(product => (product?.productName || '').toLowerCase().includes(query.trim().toLowerCase()))
        .map(product => onlyAttention ? { ...product, batches: product.batches.filter(needsAttention) } : product)
        .filter(product => product.batches.length > 0),
    [stock, query, onlyAttention]);

    const totals = useMemo(() => {
        const batches = (stock?.products || []).flatMap(product => product.batches);
        return {
            products: stock?.products?.length || 0,
            quantity: batches.reduce((sum, batch) => sum + batch.quantity, 0),
            value: batches.reduce((sum, batch) => sum + (getBatchValue(batch) || 0), 0),
            expired: batches.filter(batch => batch.expired).reduce((sum, batch) => sum + batch.quantity, 0)
        };
    }, [stock]);

    const toggleProduct = (productId) => setOpenProducts(prevState => prevState.includes(productId)
        ? prevState.filter(id => id !== productId)
        : [...prevState, productId]);

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
            <PageHeader
                title={ stock ? getStockLocationName(stock.warehouse, warehouseId) : 'Stan magazynu' }
                showBack={ true }
                onBack={ () => navigate(ROUTE_WAREHOUSE_STOCKS) }
                titleAdornment={ stock && <Chip variant="outlined" label={ getWarehouseTypeLabel(stock.warehouse) }/> }
            />

            { getWarehouseStock?.loading && <LinearProgress/> }

            {
                stock && (
                    <>
                        <Grid container spacing={ 2 }>
                            <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                                <StatTile label="Produkty" value={ formatNumber(totals.products) } icon={ <CategoryIcon/> } color="secondary"/>
                            </Grid>
                            <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                                <StatTile label="Sztuk na stanie" value={ formatNumber(totals.quantity) } icon={ <Inventory2Icon/> } color="primary"/>
                            </Grid>
                            <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                                <StatTile label="Wartość netto zakupu" value={ formatCurrency(totals.value) } icon={ <PaymentsIcon/> } color="info"/>
                            </Grid>
                            <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                                <StatTile label="Sztuk przeterminowanych" value={ formatNumber(totals.expired) } icon={ <EventBusyIcon/> } color="error" valueColor={ totals.expired > 0 ? 'error.main' : undefined }/>
                            </Grid>
                        </Grid>

                        <FilterPaper title="Filtry produktów" sx={ { mb: 0 } }>
                            <Grid container spacing={ 3 } alignItems="center">
                                <Grid item xs={ 12 } sm={ 6 }>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Szukaj po nazwie produktu"
                                        value={ query }
                                        onChange={ event => setQuery(event.target.value) }
                                        InputProps={ { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action"/></InputAdornment> } }
                                    />
                                </Grid>
                                <Grid item xs={ 12 } sm={ 6 }>
                                    <FormControlLabel
                                        control={ <Switch checked={ onlyAttention } onChange={ event => setOnlyAttention(event.target.checked) }/> }
                                        label="Tylko przeterminowane i kończące się"
                                    />
                                </Grid>
                            </Grid>
                        </FilterPaper>

                        <TableContainer component={ Paper } variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                        <TableCell padding="checkbox"/>
                                        <TableCell>Produkt</TableCell>
                                        <TableCell align="right">Sztuki</TableCell>
                                        <TableCell align="right">Partie</TableCell>
                                        <TableCell align="center">Najbliższy termin</TableCell>
                                        <TableCell align="right">Przeterminowane</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        products.map(product => (
                                            <ProductRow
                                                key={ product?.productId }
                                                product={ product }
                                                open={ openProducts.includes(product?.productId) }
                                                onToggle={ () => toggleProduct(product?.productId) }
                                            />
                                        ))
                                    }
                                    {
                                        products.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={ 6 } align="center" sx={ { py: 4, color: 'text.secondary' } }>
                                                    { stock.products.length === 0 ? 'Magazyn jest pusty' : 'Brak produktów spełniających kryteria' }
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )
            }
        </Box>
    );
};

export default WarehouseStockPage;
